import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, Eye, House, Lock, Heart } from "@phosphor-icons/react";
import MarketingLayout from "@/components/MarketingLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SHOTS = [
  {
    url: "https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/20ya2mjw_Screenshot_20260619-111151.png",
    title: "A cozy welcome",
    caption: "The home screen sets the tone — warm, intentional, never sterile.",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/67zzomwo_Screenshot_20260619-111559.png",
    title: "Rooms that feel like rooms",
    caption: "Each location gets a cover photo so the Kitchen actually looks like your kitchen.",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/u5o30s66_Screenshot_20260619-111205.png",
    title: "Spots inside spots",
    caption: "Drill down: Kitchen → Pantry → Top shelf. Things live where they actually live.",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/7fifyb23_Screenshot_20260619-111528.png",
    title: "Print QR labels",
    caption: "Pick rooms or items and print a sheet. Stick them on bins, scan with your phone.",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_house-organizer-2/artifacts/mhiwrqvt_Screenshot_20260619-111538.png",
    title: "Scan to peek inside",
    caption: "Point the camera at any Nestled label and instantly see what's in the box.",
  },
];

export default function TryDemo() {
  const { demoLogin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const onTryDemo = async () => {
    setLoading(true);
    const ok = await demoLogin();
    setLoading(false);
    if (ok) {
      toast.success("Signed into the demo account — explore away!");
      navigate("/");
    } else {
      toast.error("Couldn't sign into the demo right now. Try again in a moment.");
    }
  };

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-terracotta-50 blur-3xl opacity-70" aria-hidden />
        <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-sage-400/15 blur-3xl" aria-hidden />

        <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-terracotta-600">
              <Sparkle weight="fill" size={14} /> Closed beta — web demo
            </span>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stoneink-900">
              Wander around a real Nestled home.
            </h1>
            <p className="mt-5 text-lg text-stoneink-700 max-w-2xl leading-relaxed">
              The cozy inventory app for the home you actually live in. Snap a photo, name the spot,
              never lose a thing again. We&apos;ve curated a Demo Family&apos;s home so you can wander
              the rooms — pantry, garage, kitchen — before signing up.
            </p>

            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button
                data-testid="try-demo-cta"
                onClick={onTryDemo}
                disabled={loading}
                className="group h-14 px-8 rounded-2xl bg-terracotta-500 hover:bg-terracotta-600 disabled:opacity-60 text-white text-base font-semibold flex items-center gap-3 transition-all active:scale-[0.98] shadow-ambient"
              >
                {loading ? "Signing you in…" : "Sign me into the demo"}
                <ArrowRight weight="bold" size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <Link
                to="/register"
                data-testid="demo-make-own-link"
                className="h-14 px-6 rounded-2xl border border-sand-300 hover:border-terracotta-500 hover:bg-sand-100 text-stoneink-900 text-base font-medium flex items-center gap-2 transition-colors"
              >
                Rather start my own home →
              </Link>
            </div>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm text-stoneink-700">
              <li className="flex items-start gap-3 rounded-2xl border border-sand-300 bg-white/70 backdrop-blur px-4 py-3">
                <Eye weight="duotone" size={20} className="text-terracotta-600 shrink-0 mt-0.5" />
                <span><strong>No sign-up.</strong> One click in, one click out.</span>
              </li>
              <li className="flex items-start gap-3 rounded-2xl border border-sand-300 bg-white/70 backdrop-blur px-4 py-3">
                <Lock weight="duotone" size={20} className="text-terracotta-600 shrink-0 mt-0.5" />
                <span><strong>Shared sandbox.</strong> Other testers may be here too — be kind.</span>
              </li>
              <li className="flex items-start gap-3 rounded-2xl border border-sand-300 bg-white/70 backdrop-blur px-4 py-3">
                <Heart weight="duotone" size={20} className="text-terracotta-600 shrink-0 mt-0.5" />
                <span><strong>Full access.</strong> Add, edit, snap photos — try the whole thing.</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="relative max-w-6xl mx-auto px-6 md:px-10 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-terracotta-600">A peek inside</span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stoneink-900">
              From the mobile app
            </h2>
            <p className="mt-2 text-stoneink-500 max-w-xl">A few frames from the iOS / Android build. Same data, designed for one thumb.</p>
          </div>
          <House weight="duotone" size={42} className="text-terracotta-600 hidden md:block" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SHOTS.map((s, i) => (
            <motion.figure
              key={s.url}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.06, duration: 0.5 }}
              data-testid={`demo-shot-${i}`}
              className="rounded-3xl border border-sand-300 bg-white p-3 shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all"
            >
              <div className="rounded-2xl overflow-hidden bg-stoneink-900/5 aspect-[9/16]">
                <img
                  src={s.url}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <figcaption className="px-2 pt-4 pb-2">
                <div className="font-heading text-lg text-stoneink-900 font-medium">{s.title}</div>
                <p className="text-sm text-stoneink-500 mt-1 leading-relaxed">{s.caption}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="mt-14 rounded-3xl border border-sand-300 bg-white p-8 md:p-10 shadow-ambient text-center">
          <h3 className="font-heading text-2xl sm:text-3xl font-semibold text-stoneink-900">Like what you see?</h3>
          <p className="mt-2 text-stoneink-700 max-w-xl mx-auto">
            Spin up your own home in under a minute. Free forever for the first 25 items.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={onTryDemo}
              disabled={loading}
              data-testid="try-demo-cta-bottom"
              className="h-12 px-6 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-semibold flex items-center gap-2 active:scale-[0.98]"
            >
              {loading ? "Signing you in…" : "Try the demo"}
              <ArrowRight weight="bold" size={16} />
            </button>
            <Link to="/register" className="h-12 px-6 rounded-xl border border-sand-300 hover:border-terracotta-500 text-stoneink-900 font-medium flex items-center" data-testid="demo-signup-link">
              Create a free account
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
